/**
 * Экран 1 раздела «Планирование сезона» — готовность данных (UC-0, AK-763).
 *
 * Wireframe (одобрен founder 26.08):
 * `docs/superpowers/prototypes/season-planning-2027/01-readiness.html`.
 * ФТ: `docs/superpowers/specs/2026-08-26-season-planning-fr-usecases.md` §UC-0,
 * FR-0.1–0.4.
 *
 * Что экран делает: до старта показывает плановику, чего не хватает и где
 * чинить. Всё считает сервер (FR-0.1) — здесь нет ни одной галочки, которую
 * ставят рукой, и ни одного порога, который фронт решает сам.
 *
 * Три вещи, которые легко сломать в этом файле:
 *
 * 1. **Красное не запирает раздел (Р28).** Кнопка «Перейти к посевному плану»
 *    активна ВСЕГДА, при любом цвете вердикта. Она меняет только подпись —
 *    человек видит, с какими дырами он входит. Задизейблить её «чтобы не
 *    считал по неполным данным» = отменить решение founder.
 * 2. **Хозяйство — из переключателя слева** (AK-866 В-3: область — из
 *    переключателя). Своего выбора хозяйства на экране нет (AK-897);
 *    `POST /scope/set` отсюда зовётся только deep-link'ом `?farm=` и только
 *    через `useScopeSetter`.
 * 3. **Ссылка «где чинить» приходит с сервера** вместе с пунктом. Собирать её
 *    здесь по `key` — значит развести список пунктов и список ссылок по двум
 *    файлам; пункт без экрана честно отдаёт `fix_url: null` и текстом говорит,
 *    кто его ведёт.
 */
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ArrowRight, RefreshCw } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { FarmScopeNotice } from '@/components/scope/FarmScopeNotice';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import { useCan } from '@/lib/auth/usePermissions';
import { formatRuDateNumeric, pluralRu } from '@/lib/dateFmt';
import { useSeasonByYear } from '@/lib/planning/useSeasonByYear';
import {
  barWidthPct,
  fixLinkTarget,
  useReadiness,
  type Readiness,
  type ReadinessBlock,
  type ReadinessItem,
  type ReadinessStatus,
} from '@/lib/planning/readiness';
import { cn } from '@/lib/utils';
import { usePlanningFarmScope } from '@/components/scope/usePlanningFarmScope';

export interface ReadinessSearch {
  /** Deep-link хозяйства: ссылку «посмотри, чего у Зимовников не хватает»
   * пересылают коллеге. Открывшись, экран переключает область сессии на это
   * хозяйство и убирает параметр (AK-897). */
  farm?: string;
}

/**
 * Цвет светофора. Три семантических токена, никакого hex: зелёный —
 * `primary` (фирменный), жёлтый — `accent-brand` («требует внимания», это НЕ
 * CTA-кнопка, политика цвета такие подсветки не касается), красный —
 * `destructive`.
 */
const DOT: Record<ReadinessStatus, string> = {
  ok: 'bg-primary',
  warn: 'bg-accent-brand',
  missing: 'bg-destructive',
};

const BAR: Record<ReadinessStatus, string> = {
  ok: 'bg-primary',
  warn: 'bg-accent-brand',
  missing: 'bg-destructive',
};

const VERDICT_CARD: Record<ReadinessStatus, string> = {
  ok: 'border-l-4 border-l-primary bg-primary/5',
  warn: 'border-l-4 border-l-accent-brand bg-accent-brand/10',
  missing: 'border-l-4 border-l-destructive bg-destructive/5',
};

const VERDICT_INK: Record<ReadinessStatus, string> = {
  ok: 'text-primary',
  warn: 'text-accent-brand',
  missing: 'text-destructive',
};

const VERDICT_EYEBROW: Record<'minimal' | 'sufficient', Record<ReadinessStatus, string>> = {
  minimal: {
    ok: 'Минимальный набор — готов',
    warn: 'Минимальный набор — с замечаниями',
    missing: 'Минимальный набор — не готов',
  },
  sufficient: {
    ok: 'Достаточный набор — готов',
    warn: 'Достаточный набор — с замечаниями',
    missing: 'Достаточный набор — не готов',
  },
};

function VerdictCard({ block }: { block: ReadinessBlock }) {
  return (
    <Card className={cn('h-full', VERDICT_CARD[block.status])}>
      <CardContent className="p-4">
        <div
          className={cn(
            'text-xs font-semibold uppercase tracking-wide',
            VERDICT_INK[block.status],
          )}
        >
          {VERDICT_EYEBROW[block.key][block.status]}
        </div>
        <h3 className="mt-1 text-sm font-bold text-foreground">{block.verdict_title}</h3>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          {block.verdict_summary}
        </p>
      </CardContent>
    </Card>
  );
}

function ItemRow({ item }: { item: ReadinessItem }) {
  const width = barWidthPct(item);
  const fix = fixLinkTarget(item.fix_url);
  return (
    <li className="grid grid-cols-[0.75rem_minmax(0,1fr)_auto] items-start gap-3 border-b border-border py-3 last:border-b-0">
      <span
        aria-hidden
        className={cn('mt-1 h-3 w-3 shrink-0 rounded-full', DOT[item.status])}
      />
      <div className="min-w-0">
        <div className="text-sm font-semibold text-foreground">{item.label}</div>
        <div className="mt-1 text-xs tabular-nums text-foreground/80">{item.value_text}</div>
        {width !== null && (
          <div
            className="mt-2 h-1 w-full max-w-56 overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-valuenow={width}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={item.label}
          >
            <span className={cn('block h-full rounded-full', BAR[item.status])} style={{ width: `${width}%` }} />
          </div>
        )}
        <div className="mt-1 text-xs text-muted-foreground">
          {[item.source, item.freshness_date ? `обновлено ${formatRuDateNumeric(item.freshness_date)}` : null]
            .filter(Boolean)
            .join(' · ')}
        </div>
        {/* Подсказка нужна и рядом со ссылкой (предшественник: «можно с
            пометкой»), и вместо неё (экрана нет) — поэтому не в `else`. */}
        {item.fix_hint && (
          <div className="mt-1 text-xs italic text-muted-foreground">{item.fix_hint}</div>
        )}
      </div>
      {fix && (
        <Link
          to={fix.to as string}
          search={fix.search as never}
          className="shrink-0 whitespace-nowrap pt-1 text-xs font-semibold text-primary hover:underline"
        >
          {item.fix_label ?? 'Открыть'} →
        </Link>
      )}
    </li>
  );
}

function BlockColumn({ block }: { block: ReadinessBlock }) {
  return (
    <section>
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border pb-2">
        <h2 className="text-base font-bold text-foreground">{block.title}</h2>
        <span className="text-xs text-muted-foreground">{block.hint}</span>
      </div>
      <ul className="mt-1">
        {block.items.map((item) => (
          <ItemRow key={item.key} item={item} />
        ))}
      </ul>
    </section>
  );
}

function Summary({ data }: { data: Readiness }) {
  return (
    <span className="text-xs text-muted-foreground">
      {data.fields_total.toLocaleString('ru-RU')}{' '}
      {pluralRu(data.fields_total, ['поле', 'поля', 'полей'])} ·{' '}
      {Math.round(data.accounting_area_ha).toLocaleString('ru-RU')} га учётной площади под
      севооборотом
    </span>
  );
}

export function ReadinessPage() {
  const params = useParams({ strict: false }) as { year?: string };
  const year = Number(params.year ?? '');
  const search = useSearch({ strict: false }) as ReadinessSearch;
  const navigate = useNavigate();

  // Право то же, что у сводки варианта плана: экран показывает плановую
  // экономику раздела и ведёт в него же (backend гейтит `tech_cards:R`).
  const canRead = useCan('tech_cards', 'r');
  const season = useSeasonByYear(year);
  /**
   * Хозяйство — из переключателя слева (AK-866 В-3: область — из
   * переключателя); `?farm=` — только присланная ссылка (AK-897). Чек-лист
   * считается ПО ХОЗЯЙСТВУ (ADR-045 D3): в переключателе компания или
   * кластер — экран просит выбрать хозяйство; одно хозяйство — подставляется.
   */
  const farmScope = usePlanningFarmScope({
    searchFarm: search.farm,
    onScopeChanged: () =>
      void navigate({
        to: '/planning/seasons/$year/readiness',
        params: { year: String(year) },
        search: {},
        replace: true,
      }),
  });
  const { farmId } = farmScope;

  const readiness = useReadiness(farmId, season.seasonId);

  const data = readiness.data;
  const minimal = data?.blocks.find((b) => b.key === 'minimal');

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-[1200px] pb-safe-fab">
        <PageHeader
          title={season.validYear ? `Готовность данных · сезон ${year}` : 'Готовность данных'}
          showBack
          breadcrumbs={[
            { label: 'Планирование сезона', to: '/planning' },
            { label: season.validYear ? `Сезон ${year}` : 'Сезон' },
            { label: 'Готовность данных' },
          ]}
          description="Чего не хватает, чтобы сесть считать план — и где это починить. Чек-лист считает система, руками в нём ничего не отмечается."
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => void readiness.refetch()}
                disabled={readiness.isFetching || !farmId || !season.seasonId}
              >
                <RefreshCw className={cn('h-4 w-4', readiness.isFetching && 'animate-spin')} />
                Пересчитать
              </Button>
              {/* Р28: кнопка активна при ЛЮБОМ цвете вердикта — красное
                  предупреждает, а не запирает вход в планирование. */}
              <Button
                size="sm"
                disabled={!season.validYear || !farmId}
                onClick={() =>
                  void navigate({
                    to: '/planning/seasons/$year/sowing-plan',
                    params: { year: String(year) },
                    search: {},
                  })
                }
              >
                Перейти к посевному плану
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          }
        />

        <Stack gap={4} className="mt-4">
          {!season.validYear && (
            <Card>
              <CardContent className="p-4 text-sm text-destructive">
                Год в адресе страницы некорректный.
              </CardContent>
            </Card>
          )}

          {season.validYear && !canRead && (
            <Card>
              <CardContent className="p-4 text-sm text-muted-foreground">
                Готовность данных вам не открыта: экран показывает плановые цены и
                технологии, а их даёт право на технологические карты. Попросите доступ у
                администратора хозяйства.
              </CardContent>
            </Card>
          )}

          {season.validYear && canRead && (
            <>
              {data && <Summary data={data} />}

              {season.isLoading && <Skeleton className="h-96 w-full" />}

              {season.resolved && !season.seasonId && (
                <Card>
                  <CardContent className="p-4 text-sm text-muted-foreground">
                    Сезон {year} ещё не открыт — планировать пока нечего. Откройте сезон в
                    настройках компании.
                  </CardContent>
                </Card>
              )}

              {season.seasonId && (
                <FarmScopeNotice
                  scope={farmScope}
                  purpose="Чек-лист готовности считается по каждому хозяйству отдельно."
                />
              )}

              {farmId && season.seasonId && readiness.isLoading && (
                <Skeleton className="h-96 w-full" />
              )}

              {farmId && season.seasonId && readiness.isError && (
                <Card>
                  <CardContent className="p-4 text-sm text-destructive">
                    Не удалось посчитать готовность данных. Обновите страницу.
                  </CardContent>
                </Card>
              )}

              {data && (
                <>
                  <div className="grid gap-3 md:grid-cols-2">
                    {data.blocks.map((block) => (
                      <VerdictCard key={block.key} block={block} />
                    ))}
                  </div>

                  <Card>
                    <CardContent className="grid gap-8 p-4 lg:grid-cols-2">
                      {data.blocks.map((block) => (
                        <BlockColumn key={block.key} block={block} />
                      ))}
                    </CardContent>
                  </Card>

                  <p className="text-xs text-muted-foreground">
                    Пересчитано {formatRuDateNumeric(data.computed_at)} · каждый пункт
                    вычисляется системой и не редактируется вручную. Ссылка «где чинить»
                    открывает нужный экран уже с фильтром.
                    {data.variant_name
                      ? ` Пункты плана считаются по варианту «${data.variant_name}».`
                      : ''}
                    {minimal?.status === 'missing'
                      ? ' Красное не мешает открыть план — оно предупреждает, что часть цифр будет неполной.'
                      : ''}
                  </p>
                </>
              )}
            </>
          )}
        </Stack>
      </div>
    </AppShell>
  );
}

export default ReadinessPage;
